import { useEffect, useState } from "react";
import { volApi, formatVolError } from "./volApi";
import { BookOpen, Search, Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Volunteers see a read-only slice of the resource directory
// (active + non-deleted + verified) so they can refer community members
// they meet while volunteering.
export default function VolunteerResources() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (search) => {
    setLoading(true);
    try {
      const { data } = await volApi.get("/volunteers/me/resources", {
        params: search ? { q: search } : {},
      });
      setItems(data || []);
    } catch (e) {
      toast.error(formatVolError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q.trim() || undefined);
  };

  return (
    <div className="space-y-6" data-testid="volunteer-resources">
      <div>
        <div className="hw-eyebrow mb-2">Resource Directory</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
          Community resources
        </h1>
        <p className="text-[#334155] mt-2 max-w-2xl">
          Trusted partners across Eastern North Carolina. Share these freely
          with anyone you meet who needs help — the HarborWell team curates
          every listing.
        </p>
      </div>

      <form onSubmit={onSearch} className="flex items-center gap-2" data-testid="volunteer-resources-search-form">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, county, or keyword"
            data-testid="volunteer-resources-search-input"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
          />
        </div>
        <button
          type="submit"
          data-testid="volunteer-resources-search-btn"
          className="px-5 py-2.5 rounded-xl bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
        >
          Search
        </button>
      </form>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white border border-[#E5E5E0] rounded-2xl h-32 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 text-center" data-testid="volunteer-resources-empty">
          <BookOpen className="w-8 h-8 mx-auto text-[#1E3A8A] mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No resources found</h3>
          <p className="text-sm text-[#334155] mt-1">
            Try a different search term or contact the HarborWell team for help.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="volunteer-resources-list">
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={`volunteer-resource-${r.id}`}
              className="bg-white border border-[#E5E5E0] rounded-2xl p-5"
            >
              <div className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] inline-block px-2 py-0.5 rounded-full uppercase tracking-wider mb-2">
                {r.category?.replace(/_/g, " ")}
              </div>
              <h3 className="font-display text-base font-semibold text-[#0F172A]">{r.name}</h3>
              {r.description && (
                <p className="text-sm text-[#334155] mt-1">{r.description}</p>
              )}
              {r.eligibility && (
                <p className="text-xs text-[#1E3A8A] mt-2">
                  <strong className="text-[#334155]">Eligibility:</strong> {r.eligibility}
                </p>
              )}
              {r.hours && (
                <p className="text-xs text-[#1E3A8A] mt-1">
                  <strong className="text-[#334155]">Hours:</strong> {r.hours}
                </p>
              )}
              <div className="mt-3 space-y-1 text-sm text-[#334155]">
                {r.phone && (
                  <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 hover:text-[#1E3A8A]">
                    <Phone className="w-3.5 h-3.5 text-[#1E3A8A]" />
                    {r.phone}
                  </a>
                )}
                {r.email && (
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-[#1E3A8A]">
                    <Mail className="w-3.5 h-3.5 text-[#1E3A8A]" />
                    {r.email}
                  </a>
                )}
                {r.address && (
                  <div className="flex items-start gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-[#1E3A8A] mt-0.5 flex-shrink-0" />
                    <span>
                      {r.address}
                      {r.county && ` · ${r.county} County`}
                    </span>
                  </div>
                )}
                {r.website && (
                  <a
                    href={r.website}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[#1E3A8A] hover:underline"
                    data-testid={`volunteer-resource-${r.id}-website`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open website
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
